/**
 * @fileoverview Evaluation Run Comparison
 * @description Diffs two evaluation runs to surface improvements and regressions.
 * @module features/AIEvaluationResults/utils/runComparison
 */

import { evaluatorKey } from './evaluationView';

/** Score movement smaller than this is treated as noise. */
export const CHANGE_THRESHOLD = 0.005;

/**
 * Classify the movement between a baseline and current score.
 * @param {number|null} current - Current score.
 * @param {number|null} baseline - Baseline score.
 * @returns {{status: string, delta: number|null}} Movement classification.
 */
function classify(current, baseline) {
  if (current === null || current === undefined || baseline === null || baseline === undefined) {
    return { status: 'not-run', delta: null };
  }
  const delta = current - baseline;
  if (delta > CHANGE_THRESHOLD) {
    return { status: 'improved', delta };
  }
  if (delta < -CHANGE_THRESHOLD) {
    return { status: 'regressed', delta };
  }
  return { status: 'unchanged', delta };
}

/**
 * Compare a current run against a baseline run.
 * @param {Object} current - Current evaluator view or stored run.
 * @param {Object} baseline - Baseline stored run.
 * @returns {Object|null} Comparison result, or null when either side is missing.
 */
export function compareRuns(current, baseline) {
  if (!current || !baseline) {
    return null;
  }

  const baselineRows = new Map(baseline.evaluators.map((row) => [row.key || evaluatorKey(row), row]));
  const rows = current.evaluators.map((row) => {
    const key = row.key || evaluatorKey(row);
    const previous = baselineRows.get(key);
    if (!previous) {
      return { key, row, baselineScore: null, currentScore: row.score, status: 'added', delta: null };
    }
    baselineRows.delete(key);
    const { status, delta } = classify(row.score, previous.score);
    return { key, row, baselineScore: previous.score, currentScore: row.score, status, delta };
  });

  const removed = Array.from(baselineRows.values()).map((row) => ({
    key: row.key || evaluatorKey(row),
    row,
    baselineScore: row.score,
    currentScore: null,
    status: 'removed',
    delta: null,
  }));

  const allRows = [...rows, ...removed];
  const countOf = (status) => allRows.filter((item) => item.status === status).length;

  return {
    baselineId: baseline.id,
    rows: allRows,
    changedRows: allRows
      .filter((item) => item.status !== 'unchanged')
      .sort((a, b) => (b.delta ?? -Infinity) - (a.delta ?? -Infinity)),
    deltaOverall: (current.summary?.overallAverage ?? 0) - (baseline.summary?.overallAverage ?? 0),
    deltaBelowTarget: (current.summary?.belowTarget ?? 0) - (baseline.summary?.belowTarget ?? 0),
    improved: countOf('improved'),
    regressed: countOf('regressed'),
    unchanged: countOf('unchanged'),
    added: countOf('added'),
    removed: countOf('removed'),
  };
}

/**
 * Format a score delta for display.
 * @param {number|null} delta - Score movement.
 * @returns {string} Signed delta label.
 */
export function formatDelta(delta) {
  if (delta === null || delta === undefined) {
    return '—';
  }
  const rounded = delta.toFixed(2);
  return delta > 0 ? `+${rounded}` : rounded;
}
